import { titleCase } from "@app/string-utils";
import { Button } from "@app/ui/shared/button";
import { Label } from "@app/ui/shared/form-group";
import { MultiSelect } from "@app/ui/shared/multi-select";
import { Pill } from "@app/ui/shared/pill";
import type { SelectOption } from "@app/ui/shared/select";

interface EnvironmentOption extends SelectOption {
  stackType?: "Dedicated" | "Shared" | "Self-Hosted";
}

export interface ControlFilterBarProps {
  environmentOptions: EnvironmentOption[];
  environmentSelectionLimit: number;
  selectedEnvironmentIds: string[];
  onEnvironmentChange: (values: string[]) => void;
  frameworkOptions: SelectOption[];
  selectedFrameworks: string[];
  onFrameworksChange: (values: string[]) => void;
  categories: string[];
  selectedCategories: string[];
  onCategoriesChange: (values: string[]) => void;
  hasPendingChanges: boolean;
  hasActiveFilters: boolean;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  className?: string;
}

export const ControlFilterBar = ({
  environmentOptions,
  environmentSelectionLimit,
  selectedEnvironmentIds,
  onEnvironmentChange,
  frameworkOptions,
  selectedFrameworks,
  onFrameworksChange,
  categories,
  selectedCategories,
  onCategoriesChange,
  hasPendingChanges,
  hasActiveFilters,
  onApplyFilters,
  onResetFilters,
  className = "",
}: ControlFilterBarProps) => {
  const categoryOptions: SelectOption[] = [
    ...categories.map((category) => ({
      value: category,
      label: titleCase(category),
    })),
  ];

  return (
    <div
      className={`relative z-20 flex flex-wrap items-end gap-4 w-full max-w-full ${className}`}
    >
      <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto min-w-0">
        <Label htmlFor="controls-environment-selector">Environment</Label>
        <MultiSelect
          id="controls-environment-selector"
          value={selectedEnvironmentIds}
          options={environmentOptions}
          onChange={(values) => onEnvironmentChange(values)}
          placeholder={
            environmentOptions.length > environmentSelectionLimit
              ? "Select Environments"
              : "All Environments"
          }
          className="w-full"
          dropdownClassName="z-30 min-w-[300px]"
          selectAllVisibleLimit={environmentSelectionLimit}
          disabledOptionTooltip="Max of 5 selected environments at a time"
          renderOptionLabel={(option) => {
            const envOption = option as EnvironmentOption;
            return (
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{envOption.label}</span>
                {envOption.stackType ? (
                  <Pill className="flex-shrink-0 border-gray-200 bg-gray-100 px-2 py-0 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                    {envOption.stackType}
                  </Pill>
                ) : null}
              </div>
            );
          }}
        />
      </div>

      <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto min-w-0">
        <Label htmlFor="controls-category-selector">Category</Label>
        <MultiSelect
          id="controls-category-selector"
          value={selectedCategories}
          options={categoryOptions}
          onChange={(values) => onCategoriesChange(values)}
          placeholder="Show All Categories"
          className="w-full"
          dropdownClassName="z-30"
        />
      </div>

      <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto min-w-0">
        <Label htmlFor="controls-framework-selector">
          Compliance Framework
        </Label>
        <MultiSelect
          id="controls-framework-selector"
          value={selectedFrameworks}
          options={frameworkOptions}
          onChange={(values) => onFrameworksChange(values)}
          placeholder="All Controls"
          className="w-full"
          dropdownClassName="z-30"
        />
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 basis-auto">
        {hasActiveFilters ? (
          <Button variant="white" size="sm" onClick={onResetFilters}>
            Reset
          </Button>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={onApplyFilters}
          disabled={!hasPendingChanges}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};
